import { useLayoutEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useChrome, rememberRoot, type BackTarget } from './chromeStore'
import { useIsDesktop } from './useIsDesktop'
import type { NavKey } from './nav'

export interface PageChromeProps {
  /** The screen's name. Phone: the large title on a root tab, or the centred
   *  name beside "‹ Back" on a pushed screen. Desktop: the toolbar's screen. */
  title: string
  /** Which sidebar item this screen belongs to. Lights it in the sidebar and
   *  gives the desktop toolbar its group ("Create › Library"). Root tabs
   *  (feed, library, alerts) also become the default Back label. */
  navKey?: NavKey
  /** Override the toolbar's group. Rarely needed: navKey supplies it. */
  group?: string
  /** A pushed screen. true goes back in history, labelled with the last root
   *  tab ("‹ Feed"); an object names the destination ("‹ Feed controls"). */
  back?: boolean | BackTarget
  /** The screen's own actions: row 2, right, on the phone; the right end of
   *  the 48px toolbar on desktop. Keep it to one or two glass buttons. */
  actions?: ReactNode
  /** false hides the phone's top and tab bars (Record). Desktop is unaffected. */
  bars?: boolean
}

let seq = 0

/**
 * Declares a screen's chrome. Render it once at the top of a page:
 *
 *   <PageChrome title="Library" navKey="library" actions={<button className="gg-act">Sort</button>} />
 *
 * Renders nothing in place. The title, Back and toolbar group go to the shell
 * through a small store; the actions are portalled into whichever bar is
 * showing (phone row 2 or the desktop toolbar), so they keep their own state,
 * handlers and context.
 */
export function PageChrome({ actions, ...rest }: PageChromeProps) {
  usePageChrome(rest)
  const desktop = useIsDesktop()
  const target = useChrome((s) => (desktop ? s.deskActions : s.phoneActions))
  if (!actions || !target) return null
  return createPortal(actions, target)
}

/** The same thing as a hook, for a screen with no actions. */
export function usePageChrome(props: Omit<PageChromeProps, 'actions'>) {
  const set = useChrome((s) => s.set)
  const { title, navKey, group, back, bars = true } = props
  const backTo = back === true ? '' : back ? back.to ?? '' : null
  const backLabel = back && back !== true ? back.label ?? '' : ''
  useLayoutEffect(() => {
    const me = ++seq
    set({
      owner: me, title, navKey, group, bars,
      back: backTo === null ? null : { to: backTo || undefined, label: backLabel || undefined },
    })
    if (backTo === null) rememberRoot(navKey)
    return () => {
      if (useChrome.getState().owner === me) set({ title: '', navKey: undefined, group: undefined, back: null, bars: true })
    }
  }, [set, title, navKey, group, bars, backTo, backLabel])
}
